import { CategoryRelated } from 'idt-ecommerce-api/catalog/categories/categories.models';

import { Media } from './medias/medias.models';
import { ProductMetadata } from './metadata/product-metadata.models';

/**
 * Resumen de conceptos y casuísticas del API Rest:
 * - MOCACO (Modelo Calidad Color):
 *   Un producto que es igual a otro (aka producto padre) pero cuyo primer color es distinto
 *   al del producto padre. Usado para mostrar varias veces un mismo producto en parrilla
 *   pero con colores distintos.
 *
 * - ESTILISMO:
 *   Es un MOCACO cuyo set de imágenes son distintas a las del producto padre.
 *   Usado para mostrar varias veces un mismo producto en parrilla pero
 *   con fotos distintas (distinto fondo por ejemplo).
 *   También se puede usar para mostrar un producto con una imagen
 *   en una categoría y con otra imagen en otra categoría.
 *
 * - BUNDLE:
 *   Tipo especial de producto que representa a un conjunto de productos. Un ejemplo de uso
 *   podría ser un pijama que tiene parte de arriba y parte de abajo y se pueden vender
 *   por separado, sin embargo se quieren mostrar en parrilla como un único producto (agrupados).
 *
 *  Nosotros trabajaremos con un modelo común para todos los tipos de productos.
 *  Así que para los MOCACOS/ESTILISMOS se hacen las transformaciones de datos necesarias
 *  para que se puedan tratar como un producto "normal".
 *  El campo bundleProducts solo se devolverá cubierto con productos en el caso de el producto
 *  sea de tipo bundle (gridElemType='BUNDLE').
 *  En el resto de casos (productos normales, mocacos, estilismos...) el campo bundleProducts devolverá
 *  un array vacío.
 *  Todas estas transformaciones se implementarán en la función `convertApiProductIfNeccesary` del
 *  transformer.
 *
 * Ref: https://axinic.central.inditex.grp/confluence/pages/viewpage.action?pageId=341483406
 */
export interface Product {
  colors: ProductColor[]; /** Listado de colores: para cada color hay un conjunto de tallas y medias */
  id: string; /** Identificador */
  longDescription?: string; /** Descripción larga */
  additionalInfo?: string; /** Información adicional del producto */
  name: string; /** Nombre */
  nameEn?: string; /** Nombre en inglés */
  priceInfo?: ProductPrice; /** El modelo consta de los valores relacionados con el precio */
  urlWeb: string; /** Url a la web. */
  productUrl?: string;
  productUrlParam?: string;
  type: ProductType; /* Indica si es un producto o un bundle de productos */
  gridElemType: ProductGridElementType; /* Indica el tipo de producto a nivel de parrilla */
  bundleProducts: Product[]; /** Lista de productos de un bundle */
  metadata: ProductMetadata; /** Metadatos de un producto: info extra útil para los servicios cocinados */
  productReference: string | undefined; /** Código de referencia del producto mostrado al usuario */
  fullReference: string | undefined; /** Código de referencia del producto completo */
  productPartNumberDetails?: ProductPartNumberDetails; /** Datos relacionados con MOCACO o ESTILISMO */
  urlCMS: string; /** Url a la web generada en CMS. */
  technicalDetails: ProductTechnicalDetailModel[]; /** Detalles técnicos del producto */
  composition: ProductCompositionModel[]; /** Composición del tejido en producto */
  care: ProductCareModel[]; /** cuidados del propducto */
  relatedProducts?: Product[]; /** productos relacionados del producto */
  relatedMocacos?: Product[]; /** Mocacos relacionados del producto (mismo producto, distinto color) */
  productType?: string; /** tipo de producto. Ej:FootWear para calzado o Clothing */
  relatedCategories?: CategoryRelated[]; /** Listado de categorías a las que pertenece el producto */
  familyName?: string; /** Nombre de la familia del producto */
  familyCode?: string; /** Código de la familia del producto */
  section?: string; /** Código de la sección */
  subFamilyName?: string; /** Nombre de la subfamilia del producto */
  subFamilyCode?: string; /** Código de la subfamilia del producto */
}

export interface ProductPrice {
  oldPrice?: number; /** Precio de la API Ecommerce - precio entero y sin decimales cortado (anterior) para promociones */
  oldPriceWithCurrencySymbol?: string; /** Precio cortado (anterior) para promociones formato y currency */
  price?: number; /** Precio de la API Ecommerce - precio entero y sin decimales */
  priceWithCurrencySymbol?: string; /** Precio con formato y currency */
  discount?: string; /** Descuento entre el oldPrice y el price */
  localePrice?: number; /** Igual que `price` pero en la moneda alternativa */
  localePriceWithCurrencySymbol?: string; /** Igual que `priceWithCurrencySymbol` pero en la moneda alternativa */
  oldLocalePrice?: number; /** Igual que `oldPrice` pero en la moneda alternativa */
  oldLocalePriceWithCurrencySymbol?: string; /** Igual que `oldPriceWithCurrencySymbol` pero en la moneda alternativa */
  prewarming?: ProductPricePrewarming;
  originalPrice?: number; /** Precio de temporada */
  originalPriceWithCurrencySymbol?: string; /** Precio de temporada con formato y currency */
  discountsPercentages?: ProductDiscountsPercentages; /** Descuento a mostrar en % */
}

export interface ProductColor {
  id: string; /** Identificador */
  name: string; /** nombre */
  sizes?: ProductSize[]; /** Listado de tallas */
  medias: Media[]; /** Listado de imágenes y videos */
  modelinfo?: ModelInfo; /** Objeto con la info de la modelo */
  styleIds: string[]; /** Listado de ids de los estilos disponibles */
}

export interface ProductSize {
  sku: string; /** Id de producto unico */
  masterSizeId?: string; /** Id de la talla */
  name?: string;
  partnumber?: string;
  isBuyable?: boolean;
  backSoon?: string | null;
  priceInfo?: ProductPrice;
  description?: string | null;
  availability?: ProductAvailabilityType;
  visibilityValue?: ProductVisibilityValue;
  typeThreshold?: string;
}

export interface ProductCompositionModel {
  name?: string | null; /** nombre de la composición de tejido */
  percentage?: string | null; /** procentaje de la composición de producto */
  part?: number | null; /** parte a la que pertenece el material */
}

export interface ProductCareModel {
  name?: string; /** nombre cuidado de producto */
  description?: string; /** descripción cuidado de producto */
  image?: string; /** imgn cuidado de producto */
}

export interface ProductTechnicalDetailModel {
  id?: string; /** id de detalle técnico */
  name?: string; /** name de detalle técnico */
  title?: string; /** título de detalle técnico */
  type?: string; /** tipo de detalle técnico */
  description?: string; /** descripción de detalle técnico */
  image?: string; /** imagen asociada de detalle técnico */
  shortDescription?: string; /** descripción corta del detalle técnico */
}

export interface ModelInfo {
  height: string|null; /** Altura de la modelo */
  name: string|null; /** Nombre de la modelo */
  size: string| null; /** Talla de la modelo */
}

export interface ProductWithCategory extends Product {
  categoryId: string;
}

export type ProductType = 'ProductBean' | 'BundleBean';

export enum ProductGridElementType {
  PRODUCT = 'PRODUCT',
  MOCACO = 'MOCACO',
  ESTILISMO = 'ESTILISMO',
  GRUCACO = 'GRUCACO',
  BUNDLE = 'BUNDLE',
  LOOKBOOK = 'LOOKBOOK',
  BANNER = 'BANNER'
}
export type ProductAvailabilityType = 'in_stock' | 'out_of_stock' | 'unknown';
export type ProductVisibilityValue = 'SOLD_OUT' | 'SHOW'| 'BACK_SOON';

export interface SendProductToAFriendRequestBody {
  mailSender: string;
  mailRecipient: string;
  message: string;
  nameSender: string;
  nameRecipient: string;
  subject: string;
}

export interface SearchProduct {
  id: string;
  name: string;
}

export interface ProductPricePrewarming {
  price: string;
  promotionId: number;
  priceWithCurrency: string;
  localePriceWithCurrency?: string;
  discountRate?: string;
  description?: string;
}

export interface ProductDiscountsPercentages {
  originalPriceDiscount?: string;
  oldPriceDiscount?: string;
}


export type ProductPartNumberDetails = Partial<Record<ProductPartNumberDetailsKeys, string>>;

export enum ProductPartNumberDetailsKeys {
  colorId = 'colorId',
  style = 'style'
}
